import * as React from 'react';
import { cva } from 'class-variance-authority';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const chipBase = cva(
  [
    'relative inline-flex items-center gap-1.5 rounded-pill px-3 text-sm font-medium whitespace-nowrap',
    '[transition:background_var(--motion-fast),border-color_var(--motion-fast),box-shadow_var(--motion-fast),transform_var(--motion-fast)]',
    'motion-reduce:[transition:none]',
    // Expands the hit area to 44px without growing the visual chip.
    'after:absolute after:-inset-1.5 after:content-[""]',
  ].join(' '),
  {
    variants: {
      selected: {
        true: 'border border-transparent bg-brand-600 text-white shadow-[0_2px_6px_-1px_rgba(79,70,229,.25)]',
        false: 'border border-border-strong bg-white text-foreground hover:border-brand-400 hover:bg-brand-50 dark:bg-card dark:hover:bg-brand-500/10',
      },
      touchTarget: {
        true: 'h-9',
        false: 'h-8',
      },
    },
    defaultVariants: { selected: false, touchTarget: false },
  },
);

interface ChipCommonProps {
  className?: string;
  touchTarget?: boolean;
  children: React.ReactNode;
}

export interface ChipButtonProps
  extends ChipCommonProps,
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> {
  selected?: boolean;
  onRemove?: never;
}

export interface ChipRemovableProps extends ChipCommonProps {
  selected?: never;
  /** Called when the trailing remove control is activated. */
  onRemove: () => void;
  /** Accessible label for the remove control, e.g. built from the translated tag name. */
  removeLabel: string;
  disabled?: boolean;
}

export type ChipProps = ChipButtonProps | ChipRemovableProps;

function isRemovable(props: ChipProps): props is ChipRemovableProps {
  return typeof (props as ChipRemovableProps).onRemove === 'function';
}

export const Chip = React.forwardRef<HTMLButtonElement, ChipProps>((props, ref) => {
  if (isRemovable(props)) {
    const { className, touchTarget = false, children, onRemove, removeLabel, disabled = false } = props;
    return (
      <span
        className={cn(
          chipBase({ selected: false, touchTarget }),
          disabled && 'border-border bg-surface-2 text-muted-foreground',
          className,
        )}
      >
        {children}
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={removeLabel}
          className={cn(
            'relative -mr-1 ml-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground',
            'transition-colors duration-(--motion-fast) hover:bg-destructive hover:text-white',
            'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:pointer-events-none disabled:opacity-50',
            'after:absolute after:-inset-3.5 after:content-[""]',
          )}
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      </span>
    );
  }

  const { className, selected = false, touchTarget = false, disabled, children, ...rest } = props;
  // role="radio" (e.g. a segmented control) carries its own aria-checked state;
  // pairing it with aria-pressed would be a conflicting ARIA state on the same element.
  const usesRadioSemantics = rest.role === 'radio' || 'aria-checked' in rest;
  return (
    <button
      type="button"
      ref={ref}
      disabled={disabled}
      aria-pressed={usesRadioSemantics ? undefined : (rest['aria-pressed'] ?? selected)}
      className={cn(
        chipBase({ selected, touchTarget }),
        'active:scale-[.96] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        disabled && 'pointer-events-none border-border bg-surface-2 text-muted-foreground shadow-none',
        className,
      )}
      {...rest}
    >
      {selected && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
      {children}
    </button>
  );
});
Chip.displayName = 'Chip';
